/**
 * Перехватчик console-методов для дублирования логов в реактивный буфер
 *
 * Monkey-patch console.log/warn/error/info — каждый вызов
 * дублируется в consoleBuffer, доступный для отображения
 * в админ-консоли. Оригинальное поведение сохраняется.
 */

// Импорт типа записи лога
import type { IConsoleEntry } from '@/modules/admin/types/IConsoleEntry'
// Реактивная обёртка Vue
import { shallowRef } from 'vue'

/** Максимальное количество записей в буфере */
const MAX_BUFFER_SIZE = 500

/** Реактивный буфер перехваченных console-записей */
export const consoleBuffer = shallowRef<IConsoleEntry[]>([])

/**
 * Нативные методы console, сохранённые до monkey-patch.
 * Вызовы через этот объект не вырезает esbuild drop: ['console'] в prod —
 * дропаются только прямые console.* в исходниках.
 */
export const nativeConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  info: console.info.bind(console),
} as const

/**
 * Сериализует аргументы console-вызова в строку
 *
 * Обрабатывает циклические ссылки и сложные объекты
 * через try/catch с фоллбэком на String()
 *
 * @param args - массив аргументов console-метода
 * @returns строковое представление аргументов
 */
function serializeArgs(args: unknown[]): string {
  // WeakSet для отслеживания уже сериализованных объектов (защита от циклических ссылок)
  const seen = new WeakSet()

  return args
    .map((arg) => {
      // 1. Строки возвращаем как есть
      if (typeof arg === 'string') return arg
      // 2. Примитивы через String()
      if (typeof arg !== 'object' || arg === null) return String(arg)
      // 3. Объекты через JSON.stringify с replacer для циклических ссылок
      try {
        return JSON.stringify(arg, (_key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return '[Circular]'
            seen.add(value)
          }
          return value
        }, 2)
      } catch {
        // 4. Если JSON.stringify всё равно упал — показываем ключи объекта
        try {
          return `{${Object.keys(arg as Record<string, unknown>).join(', ')}}`
        } catch {
          return '[Объект]'
        }
      }
    })
    .join(' ')
}

/**
 * Добавляет запись в буфер консоли
 *
 * При превышении MAX_BUFFER_SIZE удаляет старые записи.
 * Перезаписывает shallowRef для тригера реактивности.
 *
 * @param level - уровень лога
 * @param args - аргументы console-вызова
 */
function pushEntry(level: IConsoleEntry['level'], args: unknown[]): void {
  // 1. Создаём новую запись
  const entry: IConsoleEntry = {
    timestamp: Date.now(),
    level,
    args: serializeArgs(args),
  }
  // 2. Добавляем в буфер с ограничением размера
  const updated = [...consoleBuffer.value, entry]
  if (updated.length > MAX_BUFFER_SIZE) {
    updated.splice(0, updated.length - MAX_BUFFER_SIZE)
  }
  // 3. Перезаписываем ref для тригера реактивности shallowRef
  consoleBuffer.value = updated
}

/**
 * Инициализирует перехват console-методов
 *
 * Сохраняет оригинальные методы и заменяет их обёртками,
 * которые вызывают оригинал + пушат запись в consoleBuffer.
 * Вызывать в самом начале main.ts, ДО создания Vue-приложения.
 */
export function initConsoleInterceptor(): void {
  // 1. Сохраняем оригинальные методы (уже в nativeConsole на уровне модуля)
  const originalLog = nativeConsole.log
  const originalWarn = nativeConsole.warn
  const originalError = nativeConsole.error
  const originalInfo = nativeConsole.info

  // 2. Заменяем каждый метод обёрткой
  console.log = (...args: unknown[]) => {
    originalLog(...args)
    pushEntry('log', args)
  }

  console.warn = (...args: unknown[]) => {
    originalWarn(...args)
    pushEntry('warn', args)
  }

  console.error = (...args: unknown[]) => {
    originalError(...args)
    pushEntry('error', args)
  }

  console.info = (...args: unknown[]) => {
    originalInfo(...args)
    pushEntry('info', args)
  }
}

/**
 * Очищает буфер консоли
 *
 * Используется кнопкой «Очистить» в AdminConsole
 */
export function clearConsoleBuffer(): void {
  consoleBuffer.value = []
}
